import * as _ from 'lodash';
import * as moment from 'moment';
import { FormEvent, useEffect, useState } from 'react';
import { Col, ControlLabel, FormControl, FormGroup, Panel, Radio, Row } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import MaskedInput from 'react-maskedinput';
import { ViewWrapper } from '../styles';
import { ILookupData } from '../lookup-data';
import { IRecipientGeneral } from '@nkr/models/index';
import { PatientType } from '@nkr/enums/index';
import AdpContent from '../../../donors/profile/donor-data/adp-content';

interface IProps {
    centerId: number,
    recipientId: number,
    lookupData: ILookupData,
    generalData: IRecipientGeneral,
    isAdminLogin: boolean,
    isInactiveReasonOutdated: boolean,
    onChange: (data: IRecipientGeneral) => void,
    newConsentFile: File,
    onViewFile: () => void,
    onChangeConsentFile: (file: File) => void,
    onRemoveConsentFile: () => void
}

const Content = (props: IProps) => {
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        setSelectedItems(props.generalData.compatiblePairKPDReasons || []);
    }, [props.generalData]);

    const handleDataChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.generalData);

        if (['pairCoordinatorId', 'inactiveReason', 'adpStatus'].indexOf(name) >= 0) {
            data[name] = parseInt(value);
        } else {
            data[name] = value;
        }

        props.onChange(data);
    };

    const handleRadioChange = (e: React.FormEvent<Radio & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.generalData);

        if (name == 'isActive') {
            data.isActive = value == '1';
        } else if (name == 'isCompatiblePair') {
            data.isCompatiblePair = value == '1';
        } else {
            data[name] = value.length > 0 ? parseInt(value) : null;
        }

        props.onChange(data);
    };

    let { generalData, lookupData } = props;

    if (generalData == null || lookupData == null) {
        return null;
    }

    let voucherStatusList       = props.lookupData.voucherStatusList.filter(x => {
        if (props.generalData.canSetVoucherActive == false && x.id == 1) {
            return false;
        }

        return true;
    });

    return (
        <>
            <ViewWrapper horizontal>
                <div>
                    {generalData.patientTypeId == PatientType.RemoteDirect && (
                        <Row>
                            <Col xs={12} lg={9}>
                                <Panel>
                                    <Panel.Heading>Voucher</Panel.Heading>
                                    <Panel.Body>
                                        <AdpContent
                                            adpFormPath={generalData.adpFormPath}
                                            voucherConsentsReviewed={generalData.voucherConsentsReviewed}
                                            newConsentFile={props.newConsentFile}
                                            onViewFile={(_) => props.onViewFile()}
                                            onChangeFile={props.onChangeConsentFile}
                                            onRemoveFile={props.onRemoveConsentFile}
                                            onConsentsReviewedChange={(_name, value) => props.onChange({ ...generalData, voucherConsentsReviewed: value })} />
                                    </Panel.Body>
                                </Panel>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col xs={12} lg={6}>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}><span className='important-highlight'>Alias:</span></Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='alias'
                                        maxLength={12}
                                        value={generalData.alias}
                                        onChange={handleDataChange}
                                    />
                                    <div style={{ fontSize: 12, marginLeft: 5 }}> *letters &amp; numbers only</div>
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6}>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}><span className='important-highlight'>Patient Is Active?</span></Col>
                                <Col xs={12} lg={7}>
                                    <div className='form-control-static'>
                                        <Radio inline name='isActive' value={1} checked={generalData.isActive} onChange={handleRadioChange}>Yes</Radio>
                                        <Radio inline name='isActive' value={0} checked={!generalData.isActive} onChange={handleRadioChange}>No</Radio>
                                    </div>
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={6}>
                            {!props.generalData.isActive && (
                                <>
                                {props.isInactiveReasonOutdated && (
                                    <div className="alert alert-danger">
                                        Inactive reason is out of date. Please ensure that the inactive reason is still accurate (or change it) and then save this form to clear it from data compliance.
                                    </div>
                                )}
                                <FormGroup>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Reason Inactive:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            name='inactiveReason'
                                            componentClass='select'
                                            value={props.generalData.inactiveReason}
                                            onChange={handleDataChange}
                                        >
                                            {lookupData.recipientInactiveReasonList.map((inactreason, index) =>
                                                <option key={`inactreason-${index}`} value={inactreason.id}>{inactreason.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroup>
                                </>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6}>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Hint/Cross Reference:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        type='text'
                                        name='crossReferenceHint'
                                        value={generalData.crossReferenceHint}
                                        onChange={handleDataChange}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={6}>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Patient MRN:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='mrn'
                                        value={generalData.mrn}
                                        onChange={handleDataChange}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={6}>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Primary Coordinator:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        name='pairCoordinatorId'
                                        componentClass='select'
                                        value={generalData.pairCoordinatorId}
                                        onChange={handleDataChange}
                                        style={{ width: 300 }}
                                    >
                                        <option value={0}>- Default Coordinator -</option>
                                        {lookupData.coordinatorList.map((coord, index) =>
                                            <option key={`coord-${index}`} value={coord.id}>{coord.fullName}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={6}>
                            {props.generalData.isAdpChip && (
                                <>
                                    <FormGroup>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Voucher Status:</Col>
                                        <Col xs={12} lg={7}>
                                            <FormControl
                                                name='adpStatus'
                                                componentClass='select'
                                                value={generalData.adpStatus}
                                                onChange={handleDataChange}
                                                style={{ width: 300 }}
                                            >
                                                <option value={0}>- SELECT -</option>
                                                {voucherStatusList.map((statusType, index) =>
                                                    <option key={`stat-${index}`} value={statusType.id}>{statusType.description}</option>
                                                )}
                                            </FormControl>

                                        </Col>
                                    </FormGroup>
                                </>
                            )}
                        </Col>
                        <Col xs={12} lg={5}></Col>
                    </Row>
                    <Row style={{ fontSize: '12px', marginTop: generalData.administrativeComments != '' ? 50 : 0 }}>
                        <Col xs={12} lg={9}>
                            <div className='form-control-static'>
                                {ReactHtmlParser(generalData.administrativeComments)}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={9}>
                            <label>Add a New Administrative Comment:</label><br />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} lg={12}>
                            <FormControl
                                name='comments'
                                value={generalData.comments}
                                style={{ height: 150 }}
                                componentClass='textarea'
                                onChange={handleDataChange}
                            />
                            <span style={{ color: 'red', fontSize: 12 }}>Admin comments are for managing center use only. Other centers cannot see these comments.</span>
                        </Col>
                    </Row>
                </div>
            </ViewWrapper>
        </>
    );
}

export default Content;
